import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";
import { RecordScreen } from "@/lib/Components/Screens/RecordScreen/RecordScreen";
import {
  CalendarDaysIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import { useRelocationRecord } from "@/app/Relocations/Hooks/useRelocationRecord";
import { useRelocationActions } from "@/app/Relocations/Hooks/useRelocationActions";
import { StopwatchIcon } from "@radix-ui/react-icons";
import { RelocationDraftWarning } from "@/app/Relocations/Components/RelocationDraftWarning";
import { Badge } from "@/components/catalyst/badge";
import { useTranslations } from "use-intl";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { RelocationStatusBadge } from "@/app/Relocations/Components/RelocationStatusBadge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link } from "@/components/catalyst/link";
import {
  ActivityIcon,
  GitBranchPlusIcon,
  ImagesIcon,
  MegaphoneIcon,
  Star,
  TruckIcon,
} from "lucide-react";
import { ImoovaImage } from "@/app/Media/Components/Image";

export function RelocationRecordScreen() {
  const { hasSupplier } = useGlobalSupplier();

  const relocation = useRelocationRecord();
  const getActions = useRelocationActions();
  const tScreen = useTranslations("relocation.recordScreen");
  const tTabs = useTranslations("relocation.recordScreen.tabs");

  return (
    <RecordScreen
      tabParams={{ relocationId: relocation.id }}
      title={
        <div className="flex items-center gap-2">
          <span>
            {tScreen("routeTitle", {
              from: relocation.departureOffice.name,
              to: relocation.deliveryOffice.name,
            })}
          </span>
          {relocation.priority !== null &&
            relocation.priority !== undefined && (
              <Star className="h-5 w-5 shrink-0 fill-yellow-400 text-yellow-400" />
            )}
        </div>
      }
      image={
        relocation.ogImage ? (
          <div className="mr-3 size-20 overflow-hidden rounded-md">
            <ImoovaImage
              media={relocation.ogImage}
              alt={relocation.reference}
            />
          </div>
        ) : null
      }
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/relocations"
                params={(prev) => prev}
              >
                {tScreen("breadcrumbRelocations")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{relocation.reference}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      }
      badges={
        <>
          <RelocationStatusBadge relocation={relocation} />
          {relocation.departure_dates.length === 0 ? (
            <Badge color="red">
              <ExclamationTriangleIcon className="h-3 w-3" />
              No departure dates
            </Badge>
          ) : null}
          {relocation.lines.length > 0 ? (
            <Badge color="zinc">
              {tScreen("plate")}{" "}
              {relocation.lines.map((r) => r.reference).join(", ")}
            </Badge>
          ) : null}

          {!hasSupplier ? (
            <Badge color="zinc">
              <SupplierIcon
                model={relocation.supplier}
                className="size-3.5 shrink-0 rounded-sm"
              />
              <Link
                className="underline"
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/settings"
                params={(prev) => prev}
                search={{ supplier: relocation.supplier.id }}
              >
                {relocation.supplier.name}
              </Link>
            </Badge>
          ) : null}
        </>
      }
      banner={<RelocationDraftWarning />}
      actionsGroups={getActions(relocation)}
      tabs={[
        {
          name: tTabs("details"),
          Icon: DocumentTextIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId",
        },
        {
          name: tTabs("destinations"),
          Icon: GitBranchPlusIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId/lines",
          notificationCount: relocation.linkedRelocationCount,
        },
        {
          name: tTabs("bookings"),
          Icon: CalendarDaysIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId/bookings",
        },
        {
          name: tTabs("proDrivers"),
          Icon: TruckIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId/pro-drivers",
          notificationCount: relocation.workOrdersCount ?? undefined,
        },

        {
          name: tTabs("activity"),
          Icon: ActivityIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId/activity",
        },
        {
          name: tTabs("images"),
          Icon: ImagesIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId/generations",
        },
        {
          name: tTabs("waitlists"),
          Icon: StopwatchIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId/waitlists",
          isAdminTab: true,
        },
        {
          name: tTabs("ads"),
          Icon: MegaphoneIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId/ads",
          isAdminTab: true,
        },
      ]}
    />
  );
}
